import { type Question as FilmmakerQuestion } from '../components/FilmmakerQuestionnaire';
import { type Question as StudioQuestion } from '../components/StudioOnboarding';

interface ScoringCriteria {
  weight: number;
  evaluator: (submission: any, studioPreferences: any) => number;
}

interface ScoreBreakdown {
  category: string;
  score: number;
  feedback: string;
  weight: number;
}

export interface SubmissionScore {
  totalScore: number;
  breakdown: ScoreBreakdown[];
  recommendations: string[];
}

const scoringRubric: Record<string, ScoringCriteria> = {
  genre: {
    weight: 0.25,
    evaluator: (submission, studio) => {
      const submissionGenres = Array.isArray(submission.genre) ? submission.genre : [submission.genre];
      const studioGenres = Array.isArray(studio.genres) ? studio.genres : [studio.genres];
      const matchCount = submissionGenres.filter(g => studioGenres.includes(g)).length;
      return (matchCount / Math.max(submissionGenres.length, 1)) * 100;
    }
  },
  budget: {
    weight: 0.2,
    evaluator: (submission, studio) => {
      const budgetRanges = studio.budgetRanges;
      if (budgetRanges.includes(submission.budget)) {
        return 100;
      }
      return 0;
    }
  },
  audience: {
    weight: 0.2,
    evaluator: (submission, studio) => {
      // Compare audience prototypes for overlap in demographics and interests
      const submissionAudience = submission.audience;
      const studioAudience = studio.targetAudience;
      
      // Simple text similarity check (can be enhanced with more sophisticated NLP)
      const similarity = calculateTextSimilarity(
        JSON.stringify(submissionAudience),
        studio.targetAudience
      );
      return similarity * 100;
    }
  },
  distribution: {
    weight: 0.15,
    evaluator: (submission, studio) => {
      const submissionChannels = Array.isArray(submission.distribution) 
        ? submission.distribution 
        : [submission.distribution];
      const studioChannels = Array.isArray(studio.distribution) 
        ? studio.distribution 
        : [studio.distribution];
      
      const matchCount = submissionChannels.filter(c => 
        studioChannels.includes(c)
      ).length;
      return (matchCount / Math.max(submissionChannels.length, 1)) * 100;
    }
  },
  impact: {
    weight: 0.2,
    evaluator: (submission, studio) => {
      // Compare impact goals with studio mission
      const similarity = calculateTextSimilarity(
        submission.impact,
        studio.mission
      );
      return similarity * 100;
    }
  }
};

function calculateTextSimilarity(text1: string, text2: string): number {
  // Simple word overlap similarity (can be enhanced with more sophisticated NLP)
  const words1 = text1.toLowerCase().split(/\W+/);
  const words2 = text2.toLowerCase().split(/\W+/);
  const overlap = words1.filter(w => words2.includes(w)).length;
  return overlap / Math.max(words1.length, words2.length);
}

function generateFeedback(category: string, score: number): string {
  if (score >= 90) {
    return `Excellent alignment with studio's ${category} preferences`;
  } else if (score >= 70) {
    return `Good alignment with studio's ${category} preferences, with room for minor adjustments`;
  } else if (score >= 50) {
    return `Moderate alignment with studio's ${category} preferences - consider revisions`;
  } else {
    return `Limited alignment with studio's ${category} preferences - significant revisions recommended`;
  }
}

function generateRecommendations(breakdown: ScoreBreakdown[]): string[] {
  const recommendations: string[] = [];
  
  breakdown.forEach(({ category, score }) => {
    if (score < 70) {
      switch (category) {
        case 'genre':
          recommendations.push('Consider how your project might better align with the studio\'s preferred genres');
          break;
        case 'budget':
          recommendations.push('Review the studio\'s typical budget ranges and adjust accordingly');
          break;
        case 'audience':
          recommendations.push('Refine your target audience to better match the studio\'s viewer base');
          break;
        case 'distribution':
          recommendations.push('Explore distribution channels that align with the studio\'s typical approach');
          break;
        case 'impact':
          recommendations.push('Strengthen the connection between your project\'s impact goals and the studio\'s mission');
          break;
      }
    }
  });

  return recommendations;
}

export function evaluateSubmission(
  submission: Record<string, any>,
  studioPreferences: Record<string, any>
): SubmissionScore {
  const breakdown: ScoreBreakdown[] = [];
  let weightedTotal = 0;
  let totalWeight = 0;

  Object.entries(scoringRubric).forEach(([category, criteria]) => {
    const rawScore = criteria.evaluator(submission, studioPreferences);
    const weightedScore = rawScore * criteria.weight;
    
    weightedTotal += weightedScore;
    totalWeight += criteria.weight;

    breakdown.push({
      category,
      score: rawScore,
      feedback: generateFeedback(category, rawScore),
      weight: criteria.weight
    });
  });

  const totalScore = weightedTotal / totalWeight;
  const recommendations = generateRecommendations(breakdown);

  return {
    totalScore,
    breakdown,
    recommendations
  };
}